import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import './enfants-info-step.css';
import FormulaireDynamique from '../formulaire-dynamique';
import {
  resolveIdCampInscriptionList,
  fetchFormulairesInscriptionData,
  buildSaveFormulaireInscriptionPayload,
  saveFormulaireInscription
} from '../../../server/services/formulaire-service';
import { PATH_FORMULAIRE_INSCRIPTION_CONFIRMATION, PATH_FORMULAIRE_INSCRIPTION_INACTIVITE } from '../../../constants/navigation-constants';
import { CACHE_KEY_PROCESS_TRANSACTION_RESULT_FICHE_SANTE } from '../../paiement/paiement-page';
import LoadingSvg from '../../svg/loading.js';
import { setAlert } from '../../actions/alert';
import { usePeriodicSave } from './hooks/usePeriodicSave';
import { useInactivityTimeout } from './hooks/useInactivityTimeout';
import { validateFieldValue, isLabelOrTitleField } from '../form-inputs/utils';
import { scrollToFirstValidationError } from '../formulaire-dynamique';

function getFormulaireFromResponse(f) {
  if (!f) return null;
  const d = f?.data ?? f;
  return Array.isArray(d) ? d[0] : d;
}

/**
 * Custom step component for Children Information in Camp de Jour form
 * Props:
 * - currentStep: current step index
 * - totalSteps: total number of steps
 * - goToNextStep: function to go to next step
 * - goToPreviousStep: function to go to previous step
 * - goToStep: function to go to a specific step
 * - formData: current form data object
 * - onChange: function to update a field value (fieldId, value)
 * - setFormData: function to update the entire form data
 * - validate: function to validate the current step
 **/
const EnfantsFicheSanteInfoStep =
  ({
    currentStep,
    totalSteps,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    resetStepperAndGoToFirstStep,
    formData,
    onChange,
    setFormData,
    validate,
    match,
    language,
    allowStepperNavigation = false
  }) => {

    const [formulaires, setFormulaires] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [validationErrors, setValidationErrors] = useState({});
    const formulaireDynamiqueRef = useRef(null);
    const history = useHistory();
    const location = useLocation();

    const [idCampInscriptionList, setIdCampInscriptionList] = useState([]);
    const [currentChildStep, setCurrentChildStep] = useState(0);
    const hasModifiedRef = useRef(false);
    const hasPrefilledFromInscriptionsRef = useRef(false);
    const formDataRef = useRef(formData);
    const dispatch = useDispatch();

    useEffect(() => {
      formDataRef.current = formData;
    }, [formData]);

    const nombreEnfantsFromList = (idCampInscriptionList && idCampInscriptionList.length > 0) ? idCampInscriptionList.length : 0;
    const nombreEnfants = nombreEnfantsFromList > 0 ? nombreEnfantsFromList : (parseInt(formData.nombreEnfants) || 0);

    useEffect(() => {
      if (currentChildStep >= nombreEnfants && nombreEnfants > 0) {
        setCurrentChildStep(Math.max(0, nombreEnfants - 1));
      }
    }, [nombreEnfants, currentChildStep]);

    useEffect(() => {
      const rawId = match?.params?.idCampInscription;
      const idFromUrl = typeof rawId === 'string'
        ? rawId.toLowerCase() : rawId;
      const { list, urlIdInvalid } = resolveIdCampInscriptionList(
        CACHE_KEY_PROCESS_TRANSACTION_RESULT_FICHE_SANTE,
        idFromUrl
      );
      setIdCampInscriptionList(list);

      if (urlIdInvalid || !list.length) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      hasPrefilledFromInscriptionsRef.current = false;

      fetchFormulairesInscriptionData(list)
        .then((responses) => {
          setFormulaires(responses || []);
        })
        .catch((err) => {
          setFormulaires([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, [match?.params?.idCampInscription]);

    useEffect(() => {
      if (!onChange || hasPrefilledFromInscriptionsRef.current || !formulaires?.length) return;
      hasPrefilledFromInscriptionsRef.current = true;
      formulaires.forEach((f, childIndex) => {
        const first = getFormulaireFromResponse(f);
        const inscriptions = first?.inscriptions || [];
        const prefix = `enfant${childIndex + 1}_`;
        (Array.isArray(inscriptions) ? inscriptions : [])
          .forEach((inscription) => {
            const idDetail = inscription.idFormulaireDetail;
            const valeur = inscription.valeur;
            if (idDetail != null && valeur !== undefined) {
              onChange(prefix + idDetail, valeur);
            }
          });
      });
    }, [onChange, formulaires]);

    useEffect(() => {
      if (!setFormData || !formulaires?.length) return;
      const first = getFormulaireFromResponse(formulaires[currentChildStep]);
      const inscriptions = first?.inscriptions || first?.Inscriptions || [];
      const prefix = `enfant${currentChildStep + 1}_`;
      setFormData((prev) => {
        const next = { ...prev };
        (Array.isArray(inscriptions) ? inscriptions : []).forEach((inscription) => {
          const idDetail = inscription.IdFormulaireDetail ?? inscription.idFormulaireDetail;
          const valeur = inscription.Valeur ?? inscription.valeur;
          if (idDetail != null && valeur !== undefined) next[prefix + idDetail] = valeur;
        });
        return next;
      });
    }, [currentChildStep, formulaires, setFormData]);

    const convertApiDataToFields = useMemo(() => {
      if (nombreEnfants === 0 || !formulaires?.length) {
        return [];
      }

      const allFields = [];

      for (let childIndex = 0; childIndex < nombreEnfants; childIndex++) {
        const prefix = `enfant${childIndex + 1}_`;
        const first = getFormulaireFromResponse(formulaires[childIndex]);
        const apiFields = first?.details || first?.Details || [];
        if (!Array.isArray(apiFields)) continue;

        allFields.push({
          typeChamp: 11, // BoldLabel
          idFormulaireDetail: `${prefix}section_title`,
          descriptionFrancaise: `Enfant ${childIndex + 1}`,
          descriptionAnglaise: `Child ${childIndex + 1}`,
          requis: false
        });

        apiFields.forEach((field) => {
          const idDetail = field.idFormulaireDetail ?? field.IdFormulaireDetail;
          const fieldId = `${prefix}${idDetail}`;
          allFields.push({
            ...field,
            idFormulaireDetail: fieldId,
            value: formData[fieldId] || undefined
          });
        });
      }

      return allFields;
    }, [nombreEnfants, formData, formulaires]);

    const prefixCurrentChild = nombreEnfants > 0 ? `enfant${currentChildStep + 1}_` : '';
    const fieldsForCurrentChild = useMemo(() => {
      if (!prefixCurrentChild || !convertApiDataToFields.length) return [];
      return convertApiDataToFields.filter(
        f => f.idFormulaireDetail && f.idFormulaireDetail.startsWith(prefixCurrentChild)
      );
    }, [convertApiDataToFields, prefixCurrentChild]);

    const handleFormChange = useCallback((newFormData) => {
      if (!onChange) return;
      hasModifiedRef.current = true;
      Object.keys(newFormData).forEach(fieldId => {
        onChange(fieldId, newFormData[fieldId]);
      });

      setValidationErrors(prev => {
        const newErrors = { ...prev };
        Object.keys(newFormData).forEach(fieldId => {
          delete newErrors[fieldId];
        });
        return newErrors;
      });
    }, [onChange]);

    const validateEnfantsFicheSanteInfoStep = useCallback(() => {
      const errors = {};

      if (!convertApiDataToFields || convertApiDataToFields.length === 0) {
        setValidationErrors({});
        return { isValid: true, errors: {} };
      }

      convertApiDataToFields.forEach(field => {
        if (isLabelOrTitleField(field)) return;

        const fieldId = field.idFormulaireDetail;
        if (!fieldId) {
          return;
        }

        const value = formData[fieldId];
        const fieldError = validateFieldValue(field, value, language);

        if (fieldError) {
          errors[fieldId] = fieldError;
        }
      });

      if (formulaireDynamiqueRef.current) {
        const nestedValidation = formulaireDynamiqueRef.current.validate();
        if (!nestedValidation.isValid) {
          Object.assign(errors, nestedValidation.errors);
        }
      }

      setValidationErrors(errors);
      return {
        isValid: Object.keys(errors).length === 0,
        errors
      };
    }, [convertApiDataToFields, formData, language]);

    const validateCurrentChildStep = useCallback(() => {
      const errors = {};
      if (!fieldsForCurrentChild.length) {
        setValidationErrors({});
        return { isValid: true, errors: {} };
      }
      fieldsForCurrentChild.forEach(field => {
        if (isLabelOrTitleField(field)) return;
        const fieldId = field.idFormulaireDetail;
        if (!fieldId) return;
        const value = formData[fieldId];
        const fieldError = validateFieldValue(field, value, language);
        if (fieldError) errors[fieldId] = fieldError;
      });
      if (formulaireDynamiqueRef.current) {
        const nested = formulaireDynamiqueRef.current.validate();
        if (!nested.isValid && nested.errors) {
          Object.keys(nested.errors).forEach(id => {
            if (id.startsWith(prefixCurrentChild)) errors[id] = nested.errors[id];
          });
        }
      }
      setValidationErrors(errors);
      return { isValid: Object.keys(errors).length === 0, errors };
    }, [fieldsForCurrentChild, formData, language, prefixCurrentChild]);


    const handlePreviousChildOrStep = useCallback(() => {
      if (currentChildStep > 0) {
        setCurrentChildStep(s => s - 1);
        setValidationErrors({});
      } else if (currentStep > 0) {
        goToPreviousStep();
      }
    }, [currentChildStep, currentStep, goToPreviousStep]);

    const renderPreviousButton = useCallback(() => {
      if (currentChildStep > 0 || currentStep > 0) {
        return (
          <button
            type="button"
            className="enfants-info-previous-button"
            onClick={handlePreviousChildOrStep}
          >
            {language === 'fr' ? 'Précédent' : 'Previous'}
          </button>
        );
      }
      return null;
    }, [currentChildStep, currentStep, goToPreviousStep, handlePreviousChildOrStep, language]);

    const scrollToFirstError = useCallback((errors) => {
      scrollToFirstValidationError(errors, {
        getStepIndexFromFieldId: (fieldId) => {
          const m = fieldId.match(/^enfant(\d+)_/);
          return m ? parseInt(m[1], 10) - 1 : null;
        },
        currentStep: currentChildStep,
        setCurrentStep: setCurrentChildStep
      });
    }, [currentChildStep]);

    const buildSavePayload = useCallback(() => {
      const currentForm = getFormulaireFromResponse(formulaires[currentChildStep]);
      if (!currentForm) return null;
      const idCampInscription = idCampInscriptionList[currentChildStep];
      return buildSaveFormulaireInscriptionPayload(
        formDataRef.current,
        idCampInscription,
        currentForm,
        currentChildStep
      );
    }, [formulaires, currentChildStep, idCampInscriptionList]);

    const saveAndThen = useCallback(async (onSuccess) => {
      setIsLoading(true);

      const formDataToUse = { ...formDataRef.current };
      if (formulaireDynamiqueRef.current?.getFormData) {
        Object.assign(formDataToUse, formulaireDynamiqueRef.current.getFormData());
      }

      const currentForm = getFormulaireFromResponse(formulaires[currentChildStep]);
      if (!currentForm) return;

      const payload = buildSaveFormulaireInscriptionPayload(
        formDataToUse,
        idCampInscriptionList[currentChildStep],
        currentForm,
        currentChildStep
      );

      try {
        const response = await saveFormulaireInscription(payload);
        const savedForm = getFormulaireFromResponse(response);
        const inscriptionsFromResponse = savedForm?.inscriptions || [];

        setFormData((prev) => {
          const next = { ...prev };
          (Array.isArray(inscriptionsFromResponse) ? inscriptionsFromResponse : []).forEach((inscription) => {
            const idDetail = inscription.idFormulaireDetail;
            const valeur = inscription.valeur;
            if (idDetail != null) {
              next[`enfant${currentChildStep + 1}_${idDetail}`] = valeur;
            }
          });
          return next;
        });

        hasModifiedRef.current = false;
        setValidationErrors(prev => {
          const next = { ...prev };
          delete next._save;
          return next;
        });
        onSuccess();
      } catch (err) {
        setValidationErrors(prev => ({
          ...prev,
          _save: err?.message || (language === 'fr'
            ? 'Erreur lors de l\'enregistrement.'
            : 'Error while saving.')
        }));
      } finally {
        setIsLoading(false);
      }
    }, [buildSavePayload, setFormData, currentChildStep, formulaires, idCampInscriptionList]);

    usePeriodicSave({
      save: () => saveAndThen(() => { }),
      getHasChanges: () => hasModifiedRef.current,
      intervalMs: 5 * 60 * 1000,
      enabled: !isLoading && formulaires.length > 0 && (idCampInscriptionList?.length > 0),
      onActivated: () => {
        const message = language === 'fr'
          ? 'Sauvegarde automatique activée.'
          : 'Auto-save enabled.';
        dispatch(setAlert(message, 'success'));
      },
    });

    const handleInactivityTimeout = useCallback(() => {
      saveAndThen(() => {
        const idZec = match?.params?.idZec || (location?.pathname && location.pathname.split('/').filter(Boolean)[0]) || '';
        const inactivityPath = `/${idZec}/${language}${PATH_FORMULAIRE_INSCRIPTION_INACTIVITE}`;
        history.push({ pathname: inactivityPath });
      });
    }, [saveAndThen, history, match?.params?.idZec, location?.pathname, language]);

    useInactivityTimeout({
      onTimeout: handleInactivityTimeout,
      timeoutMs: 15 * 60 * 1000,
      enabled: !isLoading && formulaires.length > 0 && (idCampInscriptionList?.length > 0),
    });

    const handleNext = () => {
      setIsLoading(true);

      const validation = validateEnfantsFicheSanteInfoStep();
      if (validation.isValid) {
        setValidationErrors({});
        goToNextStep();
      } else {
        scrollToFirstError(validation.errors);
      }

      setIsLoading(false);
    };

    const handleNextOrConfirm = () => {
      setIsLoading(true);

      if (currentChildStep < nombreEnfants - 1) {
        const validation = validateCurrentChildStep();
        if (validation.isValid) {
          setValidationErrors({});
          saveAndThen(() => setCurrentChildStep(s => s + 1));
        } else {
          setValidationErrors(validation.errors);
          scrollToFirstError(validation.errors);
          setIsLoading(false);
        }
      } else {
        const validation = validateEnfantsFicheSanteInfoStep();
        if (validation.isValid) {
          setValidationErrors({});
          saveAndThen(handleNext);
        } else {
          setValidationErrors(validation.errors);
          scrollToFirstError(validation.errors);
          setIsLoading(false);
        }
      }
    };

    const handleStepperStepClick = (targetChildIndex) => {
      if (!allowStepperNavigation) return;
      if (targetChildIndex === currentChildStep) return;
      if (targetChildIndex < currentChildStep) {
        setValidationErrors({});
        setCurrentChildStep(targetChildIndex);
        return;
      }
      const validation = validateCurrentChildStep();
      if (validation.isValid) {
        setValidationErrors({});
        setCurrentChildStep(targetChildIndex);
      } else {
        setValidationErrors(validation.errors);
        scrollToFirstError(validation.errors);
      }
    };

    const getConfirmationPath = useCallback(() => {
      const idZec = match?.params?.idZec || (location?.pathname && location.pathname.split('/').filter(Boolean)[0]) || '';
      return `/${idZec}/${language}${PATH_FORMULAIRE_INSCRIPTION_CONFIRMATION}`;
    }, [match?.params?.idZec, location?.pathname, language]);

    const handleSubmit = async () => {

      const validation = validateEnfantsFicheSanteInfoStep();
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        scrollToFirstError(validation.errors);
        return;
      }
      setValidationErrors({});

      await saveAndThen(() => {
        history.push({ pathname: getConfirmationPath() });
      });
    };

    // const handleTestSave = useCallback(() => {
    //   saveAndThen(() => {
    //     const message = language === 'fr'
    //       ? 'Sauvegarde test réussie.'
    //       : 'Test save successful.';
    //     dispatch(setAlert(message, 'success'));
    //   });
    // }, [saveAndThen, language, dispatch]);

    if (isLoading && formulaires.length <= 0) {
      return (
        <div className="enfants-info-step">
          <LoadingSvg height="90px" margin="0" display="block" background="transparent" />
        </div>
      );
    }
    if (formulaires.length === 0 || nombreEnfants <= 0) {
      return (
        <div className="enfants-info-step">
          <h2>{language === 'fr' ? 'Fiche de santé de l\'enfant' : 'Health information for the child'}</h2>
          <p className="enfants-info-color-error">
            {language === 'fr' ? 'Aucune donnée trouvée.' : 'No data found.'}
          </p>
        </div>
      );
    }

    const isLastChild = currentChildStep === nombreEnfants - 1;
    const isLastMainStep = currentStep === totalSteps - 1;
    const showConfirmButton = isLastChild && isLastMainStep;

    const stepTitle = nombreEnfants > 1
      ? (language === 'fr' ? 'Fiche de santé des enfants' : 'Health information for the children')
      : (language === 'fr' ? 'Fiche de santé de l\'enfant' : 'Health information for the child');

    return (
      <div className="enfants-info-step">
        {isLoading && (
          <span className="enfants-info-loading">
            <LoadingSvg height="90px" margin="0" display="block" background="transparent" />
          </span>
        )}
        <h2>{stepTitle}</h2>

        {nombreEnfants > 1 && (
          <div
            className={`enfants-info-stepper ${!allowStepperNavigation ? 'enfants-info-stepper--no-nav' : ''}`}
            role="tablist"
            aria-label={language === 'fr' ? 'Enfants' : 'Children'}
          >
            {Array.from({ length: nombreEnfants }, (_, i) => i + 1)
              .map((childNum) => {
                const stepIndex = childNum - 1;
                const isActive = currentChildStep === stepIndex;
                return (
                  <button
                    key={childNum}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-label={`${language === 'fr' ? 'Enfant' : 'Child'} ${childNum}`}
                    className={`enfants-info-stepper-step ${isActive ? 'enfants-info-stepper-step--active' : ''}`}
                    onClick={() => handleStepperStepClick(stepIndex)}
                  >
                    <span className="enfants-info-stepper-step-number">{childNum}</span>
                    <span className="enfants-info-stepper-step-label">
                      {language === 'fr' ? 'Enfant' : 'Child'} {childNum}
                    </span>
                  </button>
                );
              })}
          </div>
        )}

        {Object.keys(validationErrors).length > 0 && (
          <div className="enfants-info-validation-summary">
            <strong>{language === 'fr' ? 'Veuillez corriger les erreurs suivantes :' : 'Please correct the following errors:'}</strong>
            <ul>
              {Object.entries(validationErrors).map(([fieldId, error]) => {
                if (fieldId === '_save') {
                  return (<li key={fieldId}>{error}</li>);
                }
                const childMatch = fieldId.match(/^enfant(\d+)_/);
                const childNumber = childMatch ? parseInt(childMatch[1]) : null;

                const field = convertApiDataToFields.find(f => f.idFormulaireDetail === fieldId);
                const description = field
                  ? (language === 'fr' ? field.descriptionFrancaise : field.descriptionAnglaise) || field.descriptionFrancaise || fieldId
                  : fieldId;

                const displayText = childNumber
                  ? `${language === 'fr' ? 'Enfant' : 'Child'} ${childNumber} - ${description}: ${error}`
                  : `${description}: ${error}`;

                return (
                  <li key={fieldId}>{displayText}</li>
                );
              })}
            </ul>
          </div>
        )}
        <FormulaireDynamique
          key={prefixCurrentChild}
          ref={formulaireDynamiqueRef}
          fields={fieldsForCurrentChild}
          onChange={handleFormChange}
          formData={formData}
          language={language}
          match={match}
          hideSubmitButton={true}
        />
        {!isLoading &&
          <div className="enfants-info-actions">
            {/* <button
              type="button"
              className="enfants-info-test-save-button"
              onClick={handleTestSave}
              disabled={isLoading}
              style={{
                backgroundColor: '#ff9800',
                color: 'white',
                border: 'none',
                padding: '8px 14px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                marginRight: '10px'
              }}
            >
              {language === 'fr' ? '🔧 Tester la sauvegarde' : '🔧 Test save'}
            </button> */}
            {renderPreviousButton()}

            {showConfirmButton ? (
              <button
                type="button"
                className="enfants-info-next-button"
                disabled={isLoading}
                onClick={handleSubmit}
              >
                {language === 'fr' ? 'Confirmer' : 'Confirm'}
              </button>
            ) : (
              <button
                type="button"
                className="enfants-info-next-button"
                disabled={isLoading}
                onClick={handleNextOrConfirm}
              >
                {language === 'fr' ? 'Suivant' : 'Next'}
              </button>
            )}
          </div>
        }
      </div>
    );
  };

EnfantsFicheSanteInfoStep.validate = (formData, language = 'fr', formulaireDetailData = null) => {
  const errors = {};

  const nombreEnfants = parseInt(formData.nombreEnfants) || 0;

  if (nombreEnfants === 0) {
    return { isValid: true, errors: {} };
  }

  if (!formulaireDetailData || formulaireDetailData.length === 0) {
    return { isValid: true, errors: {} };
  }

  for (let childIndex = 0; childIndex < nombreEnfants; childIndex++) {
    const prefix = `enfant${childIndex + 1}_`;
    const detailsForChild = Array.isArray(formulaireDetailData?.[childIndex])
      ? formulaireDetailData[childIndex]
      : formulaireDetailData;

    (detailsForChild || []).forEach(apiField => {
      if (isLabelOrTitleField(apiField)) return;

      const idDetail = apiField.idFormulaireDetail ?? apiField.IdFormulaireDetail;
      if (!idDetail) return;
      const fieldId = `${prefix}${idDetail}`;
      const value = formData[fieldId];

      const field = {
        typeChamp: apiField.typeChamp,
        requis: apiField.requis,
        min: apiField.min,
        max: apiField.max
      };

      const fieldError = validateFieldValue(field, value, language);

      if (fieldError) {
        errors[fieldId] = fieldError;
      }
    });
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export default EnfantsFicheSanteInfoStep;